import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { BRAND } from "../lib/brand";
import { LogOut, ExternalLink, Users, FileText, Lock, ShoppingBag, Camera } from "lucide-react";
import { Toaster } from "sonner";

const tabs = [
    { to: "/admin", label: "Members", icon: Users, testid: "tab-members" },
    { to: "/admin/merch", label: "Merchandise", icon: ShoppingBag, testid: "tab-merch" },
    { to: "/admin/gallery", label: "Gallery", icon: Camera, testid: "tab-gallery" },
    { to: "/admin/content", label: "Content", icon: FileText, testid: "tab-content" },
    { to: "/admin/password", label: "Password", icon: Lock, testid: "tab-password" },
];

export default function AdminLayout({ children }) {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const { pathname } = useLocation();

    const onLogout = async () => {
        await logout();
        navigate("/admin/login");
    };

    return (
        <div className="min-h-screen bg-meraki-bg text-meraki-text">
            <Toaster theme="dark" position="top-right" />
            <div className="sticky top-0 z-30 bg-black/85 backdrop-blur-xl border-b border-meraki-gold/20">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-3" data-testid="admin-logo-link">
                        <img src={BRAND.logo} alt="" className="w-10 h-10 object-contain" />
                        <div>
                            <div className="font-display text-meraki-gold text-lg leading-none">MERAKI</div>
                            <div className="text-[9px] uppercase tracking-[0.3em] text-meraki-muted mt-1">Admin Panel</div>
                        </div>
                    </Link>
                    <div className="flex items-center gap-4">
                        <a href="/" target="_blank" rel="noreferrer" className="text-[10px] uppercase tracking-[0.25em] text-meraki-muted hover:text-meraki-gold transition-colors hidden md:inline-flex items-center gap-1">
                            <ExternalLink className="w-3 h-3" /> View Site
                        </a>
                        <span className="text-xs text-meraki-muted hidden md:inline">{user?.email}</span>
                        <button
                            data-testid="admin-logout-btn"
                            onClick={onLogout}
                            className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-meraki-text hover:text-meraki-gold transition-colors border border-meraki-gold/30 px-3 py-2 hover:border-meraki-gold"
                        >
                            <LogOut className="w-3 h-3" />
                            <span className="hidden sm:inline">Logout</span>
                        </button>
                    </div>
                </div>
                <div className="max-w-[1400px] mx-auto px-6 md:px-12 border-t border-meraki-gold/15">
                    <div className="flex items-center gap-1 overflow-x-auto">
                        {tabs.map((t) => {
                            const active = pathname === t.to;
                            const Icon = t.icon;
                            return (
                                <Link
                                    key={t.to}
                                    to={t.to}
                                    data-testid={t.testid}
                                    className={`flex items-center gap-2 px-4 py-3 text-xs uppercase tracking-[0.25em] whitespace-nowrap border-b-2 transition-colors ${
                                        active
                                            ? "border-meraki-gold text-meraki-gold"
                                            : "border-transparent text-meraki-muted hover:text-meraki-text"
                                    }`}
                                >
                                    <Icon className="w-3 h-3" />
                                    {t.label}
                                </Link>
                            );
                        })}
                    </div>
                </div>
            </div>

            <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-12">{children}</div>
        </div>
    );
}
